import type { DataObjectSummary, SchemaFieldSummary } from "./types";

const isEmail: (str: string) => boolean = require("validator/lib/isEmail");

export interface DataObjectError {
    name: string;
    errors: string;
}

export function buildFieldValueMap(
    values: Record<string, unknown>,
    schemaFields: SchemaFieldSummary[],
): { mapped: Record<string, unknown>; unknownFieldNames: string[] } {
    const mapped: Record<string, unknown> = {};
    const unknownFieldNames: string[] = [];
    Object.entries(values || {}).forEach(([fieldName, value]) => {
        const sf = schemaFields.find((f) => (f?.name || "").toLowerCase() === (fieldName || "").toLowerCase());
        if (!sf || !sf.fieldId) {
            unknownFieldNames.push(fieldName);
            return;
        }
        mapped[sf.fieldId] = coerceValueForField(value, sf.dataType);
    });
    return { mapped, unknownFieldNames };
}

function coerceValueForField(value: unknown, dataType?: string): unknown {
    if (value === null || value === undefined) return value;
    if (dataType === "integer" || dataType === "float" || dataType === "currency") {
        if (typeof value === "number") return value;
        const s = String(value).trim();
        if (s === "") return null;
        const n = Number(s);
        return Number.isFinite(n) ? n : value;
    }
    if (dataType === "boolean") {
        if (typeof value === "boolean") return value;
        const s = String(value).trim().toLowerCase();
        if (["true", "yes", "y", "1", "t"].includes(s)) return true;
        if (["false", "no", "n", "0", "f"].includes(s)) return false;
        return value;
    }
    return value;
}

export function buildCreateDataObjectPayload(
    values: Record<string, unknown>,
    schemaFields: SchemaFieldSummary[],
    context: { businessAreaId: string; schemaId: string; dataSetId: string },
): { payload: Record<string, unknown>; unknownFieldNames: string[] } {
    const { mapped, unknownFieldNames } = buildFieldValueMap(values, schemaFields);
    return {
        payload: {
            ...mapped,
            businessAreaId: context.businessAreaId,
            schemaId: context.schemaId,
            dataSetId: context.dataSetId,
        },
        unknownFieldNames,
    };
}

function validateFieldValue(value: unknown, field: SchemaFieldSummary): string[] {
    const messages: string[] = [];
    const isEmpty = value === null || value === undefined || (typeof value === "string" && value.trim() === "");

    if (field.isRequired && isEmpty) {
        messages.push("Value is required");
        return messages;
    }
    if (isEmpty) {
        return messages;
    }

    const str = String(value);
    if (field.dataType === "email") {
        if (!isEmail(str)) {
            messages.push("Value should be a valid email");
        }
    }
    if (typeof field.min === "number" && str.length < field.min) {
        messages.push(`Value should be minimum ${field.min} characters long`);
    }
    if (typeof field.max === "number" && str.length > field.max) {
        messages.push(`Value should be maximum ${field.max} characters long`);
    }
    return messages;
}

export function reconcileErrors(
    existing: DataObjectSummary,
    mappedValues: Record<string, unknown>,
    schemaFields: SchemaFieldSummary[],
): DataObjectError[] {
    const editedFieldIds = Object.keys(mappedValues);

    const originalErrors: DataObjectError[] = Array.isArray((existing as any).errors)
        ? ((existing as any).errors as DataObjectError[])
        : [];
    const pendingErrors = originalErrors.filter((e) => e && editedFieldIds.indexOf(e.name) === -1);

    const freshErrors: DataObjectError[] = [];
    for (const fieldId of editedFieldIds) {
        const field = schemaFields.find((f) => f?.fieldId === fieldId);
        if (!field) continue;
        const msgs = validateFieldValue(mappedValues[fieldId], field);
        if (msgs.length > 0) {
            freshErrors.push({ name: fieldId, errors: msgs.join("\n") });
        }
    }

    return [...freshErrors, ...pendingErrors];
}

export function buildUpdateDataObjectPayload(
    existing: DataObjectSummary,
    values: Record<string, unknown>,
    schemaFields: SchemaFieldSummary[],
): { payload: Record<string, unknown>; unknownFieldNames: string[] } {
    const { mapped, unknownFieldNames } = buildFieldValueMap(values, schemaFields);
    const rawVersion = (existing as any).version;
    if (rawVersion === undefined || rawVersion === null) {
        throw new Error(`Data object ${existing.dataObjectId} is missing a version; cannot update.`);
    }
    const versionNumber = typeof rawVersion === "number" ? rawVersion : Number(rawVersion);
    if (!Number.isFinite(versionNumber)) {
        throw new Error(`Data object ${existing.dataObjectId} has a non-numeric version; cannot update.`);
    }

    const errors = reconcileErrors(existing, mapped, schemaFields);
    return {
        payload: {
            ...mapped,
            errors,
            version: versionNumber,
        },
        unknownFieldNames,
    };
}
